import { useState, useCallback, useEffect } from 'react';
import { useLocalObservable } from 'mobx-react';
import EmailFormDef from '../../../forms/email-settings';
import { sanitizeEmailValues } from './utils';
import { REPORT_EMAIL_TYPES } from './constants';

const reportEmailFormValues = (settings = {}, defaults = {}) => {
    const values = {};
    REPORT_EMAIL_TYPES.forEach((type) => {
        const subjectKey = `${type.key}_subject`;
        const savedSubject = settings[`ui.email.${subjectKey}`];
        const defaultSubject = defaults[`ui.email.${subjectKey}`];
        values[subjectKey] =
            savedSubject != null && String(savedSubject) !== ''
                ? savedSubject
                : defaultSubject || savedSubject;
        values[`${type.key}_text`] = settings[`ui.email.${type.key}_text`];
        values[`${type.key}_textEnabled`] =
            settings[`ui.email.${type.key}_textEnabled`];
        values[`mrkdwn_${type.key}_text`] =
            settings[`ui.email.mrkdwn_${type.key}_text`];
    });
    return values;
};

export const useEmailForm = (store, companyStore, companyId) => {
    const [initialValue, setInitialValue] = useState({});

    const localStore = useLocalObservable(() => {
        let _form = new EmailFormDef(
            {},
            {
                options: {
                    validateOnChange: true,
                    validateOnBlur: true,
                    showErrorsOnChange: true,
                    showErrorsOnBlur: true,
                },
                hooks: {
                    onSuccess: async () => {
                        const formValues = _form.values();
                        const sanitizedValues = {
                            ...sanitizeEmailValues(formValues),
                            trainingHeaderEnabled: formValues.trainingHeaderEnabled,
                            trainingFooterEnabled: formValues.trainingFooterEnabled,
                            reminderTrainingHeaderEnabled: formValues.reminderTrainingHeaderEnabled,
                            reminderTrainingFooterEnabled: formValues.reminderTrainingFooterEnabled,
                            assessmentHeaderEnabled: formValues.assessmentHeaderEnabled,
                            assessmentFooterEnabled: formValues.assessmentFooterEnabled,
                            reminderAssessmentHeaderEnabled: formValues.reminderAssessmentHeaderEnabled,
                            reminderAssessmentFooterEnabled: formValues.reminderAssessmentFooterEnabled,
                            welcomeMessageEnabled: formValues.welcomeMessageEnabled,
                        };
                        await companyStore.saveEmailSettings(companyId, sanitizedValues);
                        doLoadData();
                    },
                },
            }
        );

        return { _form };
    });

    const doLoadData = useCallback(async () => {
        const settings = store.currentCompanyUISettings;
        const defaults = companyStore.defaultEmailSettings || {};
        localStore._form.update({
            trainingHeader: settings['ui.email.trainingHeader'],
            trainingFooter: settings['ui.email.trainingFooter'],
            reminderTrainingHeader: settings['ui.email.reminderTrainingHeader'],
            reminderTrainingFooter: settings['ui.email.reminderTrainingFooter'],
            reminderAssessmentHeader: settings['ui.email.reminderAssessmentHeader'],
            reminderAssessmentFooter: settings['ui.email.reminderAssessmentFooter'],
            assessmentHeader: settings['ui.email.assessmentHeader'],
            assessmentFooter: settings['ui.email.assessmentFooter'],
            welcomeMessage: settings['ui.email.welcomeMessage'],
            mrkdwn_trainingHeader: settings['ui.email.mrkdwn_trainingHeader'],
            mrkdwn_trainingFooter: settings['ui.email.mrkdwn_trainingFooter'],
            mrkdwn_reminderTrainingHeader: settings['ui.email.mrkdwn_reminderTrainingHeader'],
            mrkdwn_reminderTrainingFooter: settings['ui.email.mrkdwn_reminderTrainingFooter'],
            mrkdwn_reminderAssessmentHeader: settings['ui.email.mrkdwn_reminderAssessmentHeader'],
            mrkdwn_reminderAssessmentFooter: settings['ui.email.mrkdwn_reminderAssessmentFooter'],
            mrkdwn_assessmentHeader: settings['ui.email.mrkdwn_assessmentHeader'],
            mrkdwn_assessmentFooter: settings['ui.email.mrkdwn_assessmentFooter'],
            mrkdwn_welcomeMessage: settings['ui.email.mrkdwn_welcomeMessage'],
            training_subject: settings['ui.email.training_subject'],
            reminder_training_subject: settings['ui.email.reminder_training_subject'],
            reminder_assessment_subject: settings['ui.email.reminder_assessment_subject'],
            assessment_subject: settings['ui.email.assessment_subject'],
            training_greeting: settings['ui.email.training_greeting'],
            reminder_training_greeting: settings['ui.email.reminder_training_greeting'],
            reminder_assessment_greeting: settings['ui.email.reminder_assessment_greeting'],
            assessment_greeting: settings['ui.email.assessment_greeting'],
            trainingHeaderEnabled: settings['ui.email.trainingHeaderEnabled'],
            trainingFooterEnabled: settings['ui.email.trainingFooterEnabled'],
            reminderTrainingHeaderEnabled: settings['ui.email.reminderTrainingHeaderEnabled'],
            reminderTrainingFooterEnabled: settings['ui.email.reminderTrainingFooterEnabled'],
            assessmentHeaderEnabled: settings['ui.email.assessmentHeaderEnabled'],
            assessmentFooterEnabled: settings['ui.email.assessmentFooterEnabled'],
            reminderAssessmentHeaderEnabled: settings['ui.email.reminderAssessmentHeaderEnabled'],
            reminderAssessmentFooterEnabled: settings['ui.email.reminderAssessmentFooterEnabled'],
            welcomeMessageEnabled: settings['ui.email.welcomeMessageEnabled'],
            ...reportEmailFormValues(settings, defaults),
        });
    }, [store.currentCompanyUISettings, localStore]);

    useEffect(() => {
        setInitialValue({
            trainingHeader: store.currentCompanyUISettings['ui.email.trainingHeader'],
            trainingFooter: store.currentCompanyUISettings['ui.email.trainingFooter'],
            reminderTrainingHeader: store.currentCompanyUISettings['ui.email.reminderTrainingHeader'],
            reminderTrainingFooter: store.currentCompanyUISettings['ui.email.reminderTrainingFooter'],
            reminderAssessmentHeader: store.currentCompanyUISettings['ui.email.reminderAssessmentHeader'],
            reminderAssessmentFooter: store.currentCompanyUISettings['ui.email.reminderAssessmentFooter'],
            assessmentHeader: store.currentCompanyUISettings['ui.email.assessmentHeader'],
            assessmentFooter: store.currentCompanyUISettings['ui.email.assessmentFooter'],
            welcomeMessage: store.currentCompanyUISettings['ui.email.welcomeMessage'],
            ...reportEmailFormValues(store.currentCompanyUISettings),
        });
        doLoadData();

        // Load default email settings
        companyStore.loadDefaultEmailSettings(companyId);
    }, [companyId]);

    useEffect(() => {
        if (companyStore.loadingDefaultEmailSettings) return;
        const defaults = companyStore.defaultEmailSettings;
        if (!defaults) return;
        const settings = store.currentCompanyUISettings || {};
        REPORT_EMAIL_TYPES.forEach((type) => {
            const fieldName = `${type.key}_subject`;
            const saved = settings[`ui.email.${fieldName}`];
            const fallback = defaults[`ui.email.${fieldName}`];
            if (!fallback) return;
            if (saved != null && String(saved) !== '') return;
            const field = localStore._form.$(fieldName);
            if (!field) return;
            if (field.value == null || field.value === '') {
                field.set(fallback);
            }
        });
    }, [
        companyStore.defaultEmailSettings,
        companyStore.loadingDefaultEmailSettings,
        store.currentCompanyUISettings,
        localStore,
    ]);

    const handleEnabledChange = useCallback((fieldName, value) => {
        setInitialValue((prev) => ({
            ...prev,
            [fieldName]: value,
        }));
    }, []);

    const handleRevertField = useCallback((fieldName, defaultValue) => {
        const field = localStore._form.$(fieldName);
        if (field) {
            field.set(defaultValue);
        }
    }, [localStore]);

    return { 
        localStore, 
        initialValue, 
        handleEnabledChange, 
        defaultValues: companyStore.defaultEmailSettings || {}, 
        handleRevertField,
        loadingDefaultValues: companyStore.loadingDefaultEmailSettings,
    };
};

